/*!
 * MV motion & polish · v1.1 · 21 Oct 2026 (v1.0 · 20 Oct 2026)
 * Loaded (deferred, ahead of the theme scripts) by snippets/mv-motion-head.liquid.
 * v1.1: product landing titles and media, in-sequence reveals on pages, articles and policies, a reading
 * progress line on articles, and a small bump on the cart count when an item is added.
 * Settings arrive in window.MVMotionConfig. Everything is progressive: if this file never
 * runs, a safety timer in the head removes html.mv-motion and the site shows as normal.
 *
 * Parts: reveal engine · heading words · media (shimmer, unveil, parallax) · header state ·
 * magnetic CTAs · add-to-cart feedback · navigation progress + page transitions · editor hooks.
 */
(function () {
  'use strict';

  if (window.MVMotion) return;

  var d = document;
  var h = d.documentElement;
  var w = window;
  var cfg = w.MVMotionConfig || {};
  var api = (w.MVMotion = { version: '1.1.0' });

  var reduceMQ = w.matchMedia('(prefers-reduced-motion: reduce)');
  var fineMQ = w.matchMedia('(hover: hover) and (pointer: fine)');
  var designMode = !!(w.Shopify && w.Shopify.designMode) || h.classList.contains('mv-design-mode');

  var motionOn = h.classList.contains('mv-motion');
  var revealOn = motionOn && cfg.reveal !== false && !reduceMQ.matches && !designMode;

  if (designMode) h.classList.add('mv-design-mode');

  /* Sections that already carry their own hand-built motion (hero, tabs, categories,
     brand and review tickers, trade band, footer, cart) keep it: no extra effects there. */
  var SKIP = [
    '.mv-hh-section',
    '.mv-ht-section',
    '.mv-cf-section',
    '.mv-bt-section',
    '.mv-rt-section',
    '[id*="mvtrade"]',
    '.shopify-section-group-header-group',
    '.shopify-section-group-footer-group',
    '#shopify-section-cart-drawer',
    'cart-drawer',
    'cart-notification',
    '.quick-add-modal',
    'product-modal',
    '.menu-drawer',
    '.predictive-search',
    '[data-mv-skip]'
  ].join(',');

  /* Section and page headings that get the word-by-word rise when they scroll into view.
     Titles at the very top of a page animate with CSS from the first paint instead. */
  var HEADINGS = [
    '.title-wrapper .title',
    '.title-wrapper-with-link .title',
    '.collection__title .title',
    '.rich-text__heading',
    '.image-with-text__heading',
    '.image-with-text__content .heading',
    'main .heading.title',
    '.banner__heading',
    '.related-products__heading',
    '.complementary-products__heading',
    '.main-page-title',
    '.collection-hero__title',
    '.mv-chero__title',
    '.mv-pl__title',
    '[data-mv-split]'
  ].join(',');

  var MEDIA = [
    '.card__media .media',
    '.product__media',
    '.collection-hero__image-container',
    '.mv-chero__media',
    '.article-card__image'
  ].join(',');

  var EDITORIAL = [
    '.image-with-text__media',
    '.banner__media',
    '.collage-card .media',
    '.collage__item .media',
    '.article-template__hero-container .media',
    '.video-section__media',
    '[data-mv-unveil]'
  ].join(',');

  var MAGNETIC = [
    '.banner__buttons .button',
    '.image-with-text__content .button',
    '.rich-text__buttons .button',
    '.collection__view-all .button',
    '.email-signup-banner .button',
    '.mv-chero__more',
    '[data-mv-magnetic]'
  ].join(',');

  /* Long-form content (pages, articles, policies): each block arrives in turn as it scrolls in. */
  var READING = [
    '.page-width--narrow > .rte > *',
    '.main-page-content > *',
    '.article-template__content > *',
    '.shopify-policy__body .rte > *'
  ].join(',');

  var SLIDERS = '.splide, slider-component, .slider, .swiper';

  var STAGGER = 70;
  var MAX_STEPS = 8;

  function readTokens() {
    var n = parseFloat(getComputedStyle(h).getPropertyValue('--mv-stagger'));
    if (!isNaN(n)) STAGGER = n;
  }

  function skipped(el) {
    return !!(el.closest && el.closest(SKIP));
  }

  function each(root, sel, fn) {
    if (!root) return;
    if (root.nodeType === 1 && root.matches && root.matches(sel)) fn(root);
    if (root.querySelectorAll) {
      var list = root.querySelectorAll(sel);
      for (var i = 0; i < list.length; i++) fn(list[i]);
    }
  }

  function onScreen(el) {
    var r = el.getBoundingClientRect();
    return r.top < w.innerHeight && r.bottom > 0 && (r.width > 0 || r.height > 0);
  }

  /* ---------------------------------------------------------------- */
  /* Reveal engine                                                     */
  /* ---------------------------------------------------------------- */
  var io = null;
  var watched = typeof WeakSet === 'function' ? new WeakSet() : null;

  function show(el, delay) {
    if (el.classList.contains('mv-in')) return;
    el.style.setProperty('--mv-delay', Math.round(delay) + 'ms');
    el.classList.add('mv-in');
    var settle = el.classList.contains('mv-unveil') ? 2100 : 1150;
    setTimeout(function () {
      if (el.classList.contains('mv-in')) el.classList.add('mv-done');
    }, delay + settle);
  }

  function hide(el) {
    el.classList.remove('mv-in', 'mv-done');
  }

  /* Cards inside a carousel come in together, so slides never arrive blank mid-swipe. */
  function showGroup(el, delay) {
    var group = el.closest(SLIDERS);
    if (!group || !el.classList.contains('animate-item')) {
      show(el, delay);
      return 1;
    }
    var items = group.querySelectorAll('.animate-item');
    var step = 0;
    for (var i = 0; i < items.length; i++) {
      if (items[i].classList.contains('mv-in')) continue;
      var visible = onScreen(items[i]);
      show(items[i], visible ? delay + Math.min(step, MAX_STEPS) * STAGGER : 0);
      if (visible) step++;
      if (io && !cfg.repeat) io.unobserve(items[i]);
    }
    return Math.max(step, 1);
  }

  function onIntersect(entries) {
    var batch = [];
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      if (e.isIntersecting) {
        batch.push(e);
      } else if (cfg.repeat && e.target.classList.contains('mv-in') && e.boundingClientRect.top > 0) {
        hide(e.target);
      }
    }
    if (!batch.length) return;
    batch.sort(function (a, b) {
      return a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left;
    });
    var step = 0;
    for (var j = 0; j < batch.length; j++) {
      var el = batch[j].target;
      if (!cfg.repeat) io.unobserve(el);
      if (el.classList.contains('mv-in')) continue;
      step += showGroup(el, Math.min(step, MAX_STEPS) * STAGGER);
    }
  }

  function observe(el) {
    if (!io) return;
    if (watched) {
      if (watched.has(el)) return;
      watched.add(el);
    } else {
      if (el.__mvWatched) return;
      el.__mvWatched = true;
    }
    io.observe(el);
  }

  function showEverything() {
    each(d, '.animate-item, [data-mv-reveal], .mv-split, .mv-unveil, .mv-accent', function (el) {
      el.classList.add('mv-in', 'mv-done');
    });
  }

  /* ---------------------------------------------------------------- */
  /* Headings: each word rises into place. Words are wrapped in place, */
  /* so the heading text (and anything that reads it) is unchanged.    */
  /* ---------------------------------------------------------------- */
  function splitHeading(el) {
    if (el.__mvSplit) return;
    el.__mvSplit = true;
    if (skipped(el) || onScreen(el)) return;
    var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text || text.length > 120) return;
    if (el.querySelector('a, button, input, select, textarea, img, svg, video, iframe, .mv-w')) return;

    /* Plain text is split into words; inline elements (highlighted words, italics) move as one
       word so their own underline, colour or background stays intact. */
    var index = 0;
    var kids = Array.prototype.slice.call(el.childNodes);
    for (var k = 0; k < kids.length; k++) {
      var node = kids[k];
      if (node.nodeType === 1) {
        if (node.tagName === 'BR' || !node.textContent.trim()) continue;
        var holder = d.createElement('span');
        holder.className = 'mv-w';
        holder.style.setProperty('--wi', Math.min(index++, 14));
        el.insertBefore(holder, node);
        holder.appendChild(node);
        continue;
      }
      if (node.nodeType !== 3) continue;
      var parts = node.nodeValue.split(/(\s+)/);
      if (parts.join('').trim() === '') continue;
      var frag = d.createDocumentFragment();
      for (var p = 0; p < parts.length; p++) {
        var part = parts[p];
        if (!part) continue;
        if (/^\s+$/.test(part)) {
          frag.appendChild(d.createTextNode(part));
          continue;
        }
        var word = d.createElement('span');
        word.className = 'mv-w';
        word.style.setProperty('--wi', Math.min(index++, 14));
        word.textContent = part;
        frag.appendChild(word);
      }
      el.replaceChild(frag, node);
    }

    el.classList.add('mv-split');
    observe(el);
  }

  function accent(el) {
    if (el.__mvAccent) return;
    el.__mvAccent = true;
    if (skipped(el) || el.closest('.product__title')) return;
    var after = getComputedStyle(el, '::after');
    if (after && after.content && after.content !== 'none' && after.content !== 'normal') return;
    var align = getComputedStyle(el).textAlign;
    el.classList.add('mv-accent');
    if (align === 'center') el.classList.add('mv-accent--center');
    else if (align === 'right' || align === 'end') el.classList.add('mv-accent--right');
    if (revealOn && !onScreen(el)) {
      observe(el);
    } else {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.classList.add('mv-in');
        });
      });
    }
  }

  function readingBlock(el) {
    if (el.__mvReading) return;
    el.__mvReading = true;
    if (skipped(el) || el.hasAttribute('data-mv-reveal') || onScreen(el)) return;
    if (/^(SCRIPT|STYLE|BR|HR)$/.test(el.tagName)) return;
    el.setAttribute('data-mv-reveal', el.tagName === 'IMG' || el.tagName === 'FIGURE' ? 'scale' : '');
  }

  /* ---------------------------------------------------------------- */
  /* Media                                                             */
  /* ---------------------------------------------------------------- */
  function mediaLoading(box) {
    if (box.__mvMedia) return;
    box.__mvMedia = true;
    if (skipped(box)) return;
    box.classList.add('mv-media');
    var img = box.querySelector('img');
    if (!img || (img.complete && img.naturalWidth > 0) || onScreen(box)) return;
    box.classList.add('mv-loading');
    var done = function () {
      box.classList.remove('mv-loading');
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
    };
    img.addEventListener('load', done);
    img.addEventListener('error', done);
    setTimeout(done, 8000);
  }

  function editorial(el) {
    if (el.__mvEditorial) return;
    el.__mvEditorial = true;
    if (skipped(el) || el.offsetWidth < 300) return;
    if (el.closest(SLIDERS) || el.closest('.product__media-list')) return;
    if (h.classList.contains('mv-parallax-on')) el.classList.add('mv-parallax');
    if (revealOn && !onScreen(el)) {
      el.classList.add('mv-unveil');
      observe(el);
    }
  }

  /* ---------------------------------------------------------------- */
  /* Magnetic CTAs (mouse only)                                        */
  /* ---------------------------------------------------------------- */
  function magnetic(el) {
    if (el.__mvMagnetic) return;
    el.__mvMagnetic = true;
    if (skipped(el)) return;
    var frame = 0;
    el.classList.add('mv-magnetic');
    el.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      var r = el.getBoundingClientRect();
      var x = (e.clientX - r.left) / r.width - 0.5;
      var y = (e.clientY - r.top) / r.height - 0.5;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(function () {
        el.classList.add('mv-magnetic--active');
        el.style.translate = (x * 12).toFixed(2) + 'px ' + (y * 8).toFixed(2) + 'px';
      });
    });
    el.addEventListener('pointerleave', function () {
      cancelAnimationFrame(frame);
      el.classList.remove('mv-magnetic--active');
      el.style.translate = '';
    });
  }

  /* ---------------------------------------------------------------- */
  /* In-page scroll links (collection intro "Read more")              */
  /* ---------------------------------------------------------------- */
  function scrollLink(link) {
    if (link.__mvScroll) return;
    link.__mvScroll = true;
    var target = d.querySelector(link.getAttribute('data-mv-scroll'));
    if (!target) return;
    link.hidden = false;
    link.addEventListener('click', function (e) {
      e.preventDefault();
      var top = target.getBoundingClientRect().top + w.pageYOffset - headerOffset() - 24;
      w.scrollTo({ top: Math.max(0, top), behavior: reduceMQ.matches ? 'auto' : 'smooth' });
    });
  }

  /* ---------------------------------------------------------------- */
  /* Discovery: runs on load, on editor section reloads and on content */
  /* the theme adds later (infinite scroll, filters, quick add, cart).  */
  /* ---------------------------------------------------------------- */
  function scan(root) {
    root = root || d;
    if (revealOn) {
      if (cfg.reveal) each(root, '.animate-section .animate-item', observe);
      each(root, READING, readingBlock);
      each(root, '[data-mv-reveal]', observe);
      if (cfg.headings) each(root, HEADINGS, splitHeading);
    }
    if (cfg.headings) each(root, HEADINGS, accent);
    each(root, MEDIA, mediaLoading);
    each(root, EDITORIAL, editorial);
    if (cfg.magnetic && fineMQ.matches && !reduceMQ.matches) each(root, MAGNETIC, magnetic);
    each(root, '[data-mv-scroll]', scrollLink);
  }

  api.refresh = function (root) {
    scan(root || d);
  };

  function watchMutations() {
    if (!('MutationObserver' in w)) return;
    var pending = [];
    var queued = false;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && n.tagName !== 'SCRIPT' && n.tagName !== 'STYLE' && n.tagName !== 'LINK' && !n.classList.contains('mv-w')) pending.push(n);
        }
      }
      if (!pending.length || queued) return;
      queued = true;
      requestAnimationFrame(function () {
        var nodes = pending;
        pending = [];
        queued = false;
        for (var k = 0; k < nodes.length; k++) {
          if (nodes[k].isConnected) scan(nodes[k]);
        }
      });
    }).observe(d.body, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------- */
  /* Header state + sticky offset for the filter sidebar               */
  /* ---------------------------------------------------------------- */
  var headerSection = null;

  function headerOffset() {
    if (!headerSection) headerSection = d.querySelector('.section-header');
    if (!headerSection) return 0;
    var pos = getComputedStyle(headerSection).position;
    if (pos !== 'sticky' && pos !== 'fixed') return 0;
    var r = headerSection.getBoundingClientRect();
    return r.bottom > 0 ? r.bottom : 0;
  }

  function headerState() {
    var ticking = false;
    var lastScrolled = null;
    var lastOffset = -1;
    var sidebars = d.querySelectorAll('.facets-vertical .facets-wrapper');
    function update() {
      ticking = false;
      var scrolled = w.pageYOffset > 12;
      if (scrolled !== lastScrolled) {
        h.classList.toggle('mv-scrolled', scrolled);
        lastScrolled = scrolled;
      }
      if (sidebars.length) {
        var offset = Math.round(headerOffset());
        if (offset !== lastOffset) {
          lastOffset = offset;
          for (var i = 0; i < sidebars.length; i++) sidebars[i].style.setProperty('--mv-sticky-top', offset + 'px');
        }
      }
    }
    w.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    w.addEventListener('resize', function () {
      sidebars = d.querySelectorAll('.facets-vertical .facets-wrapper');
      requestAnimationFrame(update);
    });
    update();
  }

  /* ---------------------------------------------------------------- */
  /* Add to cart: gold progress line while busy, ring pulse on success */
  /* ---------------------------------------------------------------- */
  function cartFeedback() {
    d.addEventListener(
      'click',
      function (e) {
        var btn = e.target.closest && e.target.closest('.product-form__submit, .quick-add__submit[type="submit"]:not([aria-haspopup])');
        if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
        if (!btn.querySelector('.mv-btn-progress')) {
          var line = d.createElement('span');
          line.className = 'mv-btn-progress';
          line.setAttribute('aria-hidden', 'true');
          btn.appendChild(line);
        }
        var started = Date.now();
        var sawBusy = false;
        (function poll() {
          var busy = btn.classList.contains('loading') || btn.getAttribute('aria-disabled') === 'true';
          if (busy) sawBusy = true;
          if (sawBusy && !busy) {
            var form = btn.closest('product-form') || btn.closest('form');
            var error = form && form.querySelector('.product-form__error-message-wrapper:not([hidden])');
            if (!error || !error.textContent.trim()) {
              btn.classList.remove('mv-added');
              void btn.offsetWidth;
              btn.classList.add('mv-added');
              setTimeout(function () {
                btn.classList.remove('mv-added');
              }, 1000);
            }
            return;
          }
          if (Date.now() - started < 9000) setTimeout(poll, 120);
        })();
      },
      true
    );
  }

  /* ---------------------------------------------------------------- */
  /* Navigation: gold progress line + product image hand-off           */
  /* ---------------------------------------------------------------- */
  var bar = null;
  var barTimer = 0;
  var lastCard = null;

  function finishBar() {
    if (!bar) return;
    clearTimeout(barTimer);
    bar.classList.remove('is-active');
    bar.classList.add('is-done');
  }

  function progressBar() {
    bar = d.createElement('div');
    bar.className = 'mv-progress';
    bar.setAttribute('aria-hidden', 'true');
    d.body.appendChild(bar);

    d.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download') || a.hasAttribute('data-no-progress')) return;
      var url;
      try {
        url = new URL(a.href, location.href);
      } catch (err) {
        return;
      }
      if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) return;
      if (url.pathname === location.pathname && url.search === location.search) return;
      if (/^\/(checkouts?|cart\/(add|change|clear|update)|account\/logout)/.test(url.pathname)) return;
      lastCard = a.closest('.card-wrapper');
      bar.classList.remove('is-done');
      void bar.offsetWidth;
      bar.classList.add('is-active');
      clearTimeout(barTimer);
      barTimer = setTimeout(finishBar, 10000);
    });

    w.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        clearTimeout(barTimer);
        bar.classList.remove('is-active', 'is-done');
      }
    });
  }

  function pageTransitions() {
    if (!cfg.transitions || reduceMQ.matches) return;
    h.classList.add('mv-vt');
    w.addEventListener('pageswap', function (e) {
      if (!e.viewTransition) return;
      var to = e.activation && e.activation.entry && e.activation.entry.url;
      if (!lastCard || !to || to.indexOf('/products/') === -1) return;
      var media = lastCard.querySelector('.card__media .media') || lastCard.querySelector('.card__media');
      if (!media || !onScreen(media)) return;
      media.style.viewTransitionName = 'mv-product-media';
      e.viewTransition.finished.then(function () {
        media.style.viewTransitionName = '';
      });
    });
  }

  /* ---------------------------------------------------------------- */
  /* Cart count: a short bump whenever the number in the header changes */
  /* ---------------------------------------------------------------- */
  function cartBubble() {
    var icon = d.getElementById('cart-icon-bubble');
    if (!icon || !('MutationObserver' in w)) return;
    var read = function () {
      var c = icon.querySelector('.cart-count-bubble');
      return c ? c.textContent.replace(/\s+/g, '') : '';
    };
    var last = read();
    new MutationObserver(function () {
      var now = read();
      if (now === last) return;
      var grew = parseInt(now, 10) > parseInt(last, 10) || (!last && now);
      last = now;
      if (!grew || reduceMQ.matches) return;
      icon.classList.remove('mv-bump');
      void icon.offsetWidth;
      icon.classList.add('mv-bump');
      setTimeout(function () {
        icon.classList.remove('mv-bump');
      }, 900);
    }).observe(icon, { childList: true, subtree: true, characterData: true });
  }

  /* ---------------------------------------------------------------- */
  /* Articles: a thin gold line shows how far through the read you are */
  /* ---------------------------------------------------------------- */
  function readingProgress() {
    var article = d.querySelector('.article-template');
    if (!article) return;
    var line = d.createElement('div');
    line.className = 'mv-read-progress';
    line.setAttribute('aria-hidden', 'true');
    d.body.appendChild(line);
    var queued = false;
    var update = function () {
      queued = false;
      var r = article.getBoundingClientRect();
      var total = r.height - w.innerHeight;
      var p = total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 0;
      line.style.transform = 'scaleX(' + p.toFixed(4) + ')';
    };
    w.addEventListener(
      'scroll',
      function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  }

  /* ---------------------------------------------------------------- */
  /* Theme editor                                                      */
  /* ---------------------------------------------------------------- */
  function editorHooks() {
    if (!designMode) return;
    d.addEventListener('shopify:section:load', function (e) {
      scan(e.target);
    });
  }

  /* ---------------------------------------------------------------- */
  /* Boot                                                              */
  /* ---------------------------------------------------------------- */
  function boot() {
    readTokens();
    if (motionOn && cfg.parallax && !reduceMQ.matches && h.getAttribute('data-mv-intensity') !== 'subtle') h.classList.add('mv-parallax-on');

    if (revealOn && 'IntersectionObserver' in w) {
      io = new IntersectionObserver(onIntersect, { rootMargin: '0px 0px -7% 0px', threshold: 0 });
    } else {
      revealOn = false;
      if (motionOn) showEverything();
    }

    scan(d);
    watchMutations();
    headerState();
    cartFeedback();
    cartBubble();
    readingProgress();
    progressBar();
    pageTransitions();
    editorHooks();

    /* Anything already scrolled past (restored scroll, anchors) or missed: show it. */
    setTimeout(function () {
      if (!revealOn) return;
      each(d, '.animate-section .animate-item:not(.mv-in), [data-mv-reveal]:not(.mv-in), .mv-split:not(.mv-in), .mv-unveil:not(.mv-in), .mv-accent:not(.mv-in)', function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < w.innerHeight && (r.width || r.height)) show(el, 0);
      });
    }, 3200);

    w.addEventListener('beforeprint', showEverything);
    if (reduceMQ.addEventListener) {
      reduceMQ.addEventListener('change', function (e) {
        if (e.matches) showEverything();
      });
    }

    api.ready = true;
    h.classList.add('mv-ready');
  }

  try {
    boot();
  } catch (err) {
    h.classList.remove('mv-motion');
    if (w.console && console.warn) console.warn('[MV motion] switched off:', err);
  }
})();
